import { type DialogComponentProps, HelperText } from '@brightdrop/bd-ui';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import RecipientsInput from './RecipientsInput';
import useStyles from './ShareViaEmailDialog.styles';
import type { ShareViaEmailEntity } from './types';

/**
 * Body of the share-via-email dialog: the "Recipients" label, the chip
 * field under it, and whatever the dialog refused to send.
 *
 * Declared at module scope rather than inline, because `BDDialog`
 * takes its body as a component type — an inline closure would be a
 * new type on every render, remounting the field and stealing focus
 * mid-keystroke. Recipients state therefore arrives through `entity`
 * instead of a closure.
 *
 * While a send is in flight the field sits in a disabled `fieldset`,
 * which locks the input and every chip's remove button at once.
 *
 * @param entity - The recipients list, the setter that owns it, any
 * message the dialog wants shown under the field, and whether a send
 * is in flight.
 */
function ShareViaEmailForm({
  entity,
}: DialogComponentProps<ShareViaEmailEntity>) {
  const { classes } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);

  if (entity === undefined) {
    return null;
  }

  return (
    <div className={classes.card}>
      <Typography className={classes.fieldLabel} variant="body_medium">
        {t('chat.share.dialog.recipientsLabel')}
      </Typography>
      <fieldset className={classes.fieldset} disabled={entity.isSending}>
        <RecipientsInput
          recipients={entity.recipients}
          onRecipientsChange={entity.onRecipientsChange}
        />
      </fieldset>
      {entity.errorMessage !== undefined && (
        // The message appears on a press the user has already made, so
        // it has to announce itself rather than wait to be found.
        <div className={classes.error} role="alert">
          <HelperText
            error
            label={entity.errorMessage}
            testId="ai-sidekick-share-recipients"
          />
        </div>
      )}
    </div>
  );
}

export default ShareViaEmailForm;
